"use strict";Object.defineProperty(exports, "__esModule", {value: true}); function _nullishCoalesce(lhs, rhsFn) { if (lhs != null) { return lhs; } else { return rhsFn(); } } function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }/* One open draft: loads it, autosaves each field edit, and keeps the server's
 * copy and the edits not yet saved apart, so a reply or a refetch never drops
 * an unsaved edit. Detection progress lives only here, never on the server. */
var _react = require('react');


var _forms = require('shared/services/forms');

var _rules = require('./rules');


/* What a refused edit says: since the form opened, an admin published it. */
const PUBLISHED_REFUSAL = 'Only org admins can change a published form.'

const SAVE_DELAY = 400
const REFETCH_DELAY = 250

/* A field edit not yet saved: the field as edited, or null when removed. */


 const useFormDraft = (orgKey, formKey, onSaveError) => {
  const [form, setFormState] = _react.useState(null)
  const [loadError, setLoadError] = _react.useState(null)
  const [, setTick] = _react.useState.call(void 0, 0)
  const [progress, setProgress] = _react.useState({})
  const [now, setNow] = _react.useState.call(void 0, () => Date.now())

  const pending = _react.useRef({})
  const pendingName = _react.useRef(null)
  const timers = _react.useRef({})
  const inFlight = _react.useRef.call(void 0, 0)
  // Every request takes a number; a reply older than the one shown is ignored.
  const seq = _react.useRef.call(void 0, 0)
  const applied = _react.useRef.call(void 0, 0)
  const onError = _react.useRef.call(void 0, onSaveError)
  onError.current = onSaveError

  const rerender = _react.useCallback.call(void 0, () => setTick(t => t + 1), [])

  const apply = _react.useCallback.call(void 0, (next, at) => {
    if (at < applied.current) return
    applied.current = at
    setFormState(next)
  }, [])

  /* Shows a form from a command's reply (ask an admin, publish). */
  const setForm = _react.useCallback.call(void 0, (next) => apply(next, ++seq.current), [apply])

  const refetch = _react.useCallback.call(void 0, () => {
    const at = ++seq.current
    return _forms.getForm.call(void 0, { OrgKey: orgKey, FormKey: formKey })
      .then(r => apply(r.Form, at))
  }, [orgKey, formKey, apply])

  _react.useEffect.call(void 0, () => {
    refetch().catch(e => setLoadError(_forms.formsErrorText.call(void 0, e, "Couldn't open this form.")))
  }, [refetch])

  const send = _react.useCallback.call(void 0, (request, done) => {
    const at = ++seq.current
    inFlight.current++
    rerender()
    _forms.editFormFields.call(void 0, { OrgKey: orgKey, FormKey: formKey, ...request })
      .then(r => { done(); apply(r.Form, at) })
      .catch(e => {
        done()
        // Refused as unauthorized: an admin published the form since it
        // opened. Say so, and show it as it is now (read-only).
        const refused = _forms.isUnauthorized.call(void 0, e)
        onError.current(_forms.formsErrorText.call(void 0, e, "Couldn't save that change. Try again.", PUBLISHED_REFUSAL))
        if (refused) refetch().catch(() => undefined)
      })
      .finally(() => { inFlight.current--; rerender() })
  }, [orgKey, formKey, apply, rerender, refetch])

  const saveField = _react.useCallback.call(void 0, (id) => {
    delete timers.current[id]
    const entry = pending.current[id]
    if (!entry) return
    // Acknowledged (or refused): drop the edit unless a newer one followed it.
    const done = () => {
      if (_optionalChain([pending, 'access', _ => _.current, 'access', _2 => _2[id], 'optionalAccess', _3 => _3.version]) === entry.version) delete pending.current[id]
    }
    send(entry.field ? { Upsert: [entry.field] } : { Remove: [id] }, done)
  }, [send])

  const saveName = _react.useCallback.call(void 0, () => {
    delete timers.current['\0name']
    const entry = pendingName.current
    if (!entry) return
    send({ Name: entry.name }, () => {
      if (_optionalChain([pendingName, 'access', _4 => _4.current, 'optionalAccess', _5 => _5.version]) === entry.version) pendingName.current = null
    })
  }, [send])

  const schedule = _react.useCallback.call(void 0, (key, save) => {
    clearTimeout(timers.current[key])
    timers.current[key] = setTimeout(save, SAVE_DELAY)
    rerender()
  }, [rerender])

  /* Saves a field as given. Any staff save clears its yellow mark. */
  const putField = _react.useCallback.call(void 0, (field) => {
    const version = (_nullishCoalesce(_optionalChain([pending, 'access', _6 => _6.current, 'access', _7 => _7[field.id], 'optionalAccess', _8 => _8.version]), () => ( 0))) + 1
    pending.current[field.id] = { field: { ...field, check: false }, version }
    schedule(field.id, () => saveField(field.id))
  }, [schedule, saveField])

  const removeField = _react.useCallback.call(void 0, (id) => {
    const version = (_nullishCoalesce(_optionalChain([pending, 'access', _9 => _9.current, 'access', _10 => _10[id], 'optionalAccess', _11 => _11.version]), () => ( 0))) + 1
    pending.current[id] = { field: null, version }
    schedule(id, () => saveField(id))
  }, [schedule, saveField])

  /* Turns a page clockwise by `degrees` (the server turns its image), with
   * that page's fields as given (their boxes turned with it), in one save. */
  const rotatePage = _react.useCallback.call(void 0, (page, degrees, pageFields) => {
    send({ ReplacePages: [{ Page: page, Rotate: degrees }], ...(pageFields.length ? { Upsert: pageFields } : {}) }, () => undefined)
  }, [send])

  const rename = _react.useCallback.call(void 0, (name) => {
    pendingName.current = { name, version: (_nullishCoalesce(_optionalChain([pendingName, 'access', _12 => _12.current, 'optionalAccess', _13 => _13.version]), () => ( 0))) + 1 }
    schedule('\0name', saveName)
  }, [schedule, saveName])

  // Leaving the editor sends whatever is still waiting.
  _react.useEffect.call(void 0, () => () => {
    Object.entries(timers.current).forEach(([key, timer]) => {
      clearTimeout(timer)
      if (key === '\0name') saveName()
      else saveField(key)
    })
  }, [saveField, saveName])

  // A notice for this draft refetches it; with Pages it also carries detection progress.
  _react.useEffect.call(void 0, () => {
    let timer
    const off = _forms.onFormChanged.call(void 0, notice => {
      if (notice.FormKey !== formKey || notice.OrgKey !== orgKey) return
      if (notice.Pages) {
        const pages = notice.Pages
        setProgress(held => _rules.mergeProgress.call(void 0, held, pages, Date.now()))
      }
      clearTimeout(timer)
      timer = setTimeout(() => { refetch().catch(() => undefined) }, REFETCH_DELAY)
    })
    return () => { off(); clearTimeout(timer) }
  }, [orgKey, formKey, refetch])

  // While a page is being read, re-check every few seconds whether it went quiet.
  const isFinding = Object.values(progress).some(p => p.State === 'finding')
  _react.useEffect.call(void 0, () => {
    if (!isFinding) return
    const timer = setInterval(() => setNow(Date.now()), _rules.STALE_MS / 12)
    return () => clearInterval(timer)
  }, [isFinding])

  // The server's fields with the unsaved edits laid over them.
  const fields = []
  const seen = new Set()
  for (const f of _nullishCoalesce(_optionalChain([form, 'optionalAccess', _14 => _14.Fields]), () => ( []))) {
    seen.add(f.id)
    const p = pending.current[f.id]
    if (!p) fields.push(f)
    else if (p.field) fields.push(p.field)
  }
  for (const [id, p] of Object.entries(pending.current)) {
    if (!seen.has(id) && p.field) fields.push(p.field)
  }

  const isSaving = inFlight.current > 0 || Object.keys(pending.current).length > 0 || !!pendingName.current
  const name = _nullishCoalesce(_nullishCoalesce(_optionalChain([pendingName, 'access', _15 => _15.current, 'optionalAccess', _16 => _16.name]), () => ( _optionalChain([form, 'optionalAccess', _17 => _17.Name]))), () => ( ''))

  return {
    form,
    loadError,
    fields,
    name,
    isSaving,
    progress,
    now,
    putField,
    removeField,
    rotatePage,
    rename,
    setForm,
  }
}; exports.useFormDraft = useFormDraft
